export enum Lang {
  en = 'en',
  de = 'de',
}

export enum Frequency {
  NEVER = 0,
  RANDOM = 0.001,
  REGULAR = 0.1,
  FREQUENT = 0.6,
  CONSTANT = 0.9,
}

export enum ActiveStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
}

export enum SuggestionStatus {
  SUBMITTED = 'SUBMITTED',
  REVIEWED = 'REVIEWED',
  REJECTED = 'REJECTED',
  APPROVED = 'APPROVED',
  NONE = 'NONE',
}

export enum YesOrNo {
  YES = 'YES',
  NO = 'NO',
}

export enum NewOrExisting {
  NEW = 'NEW',
  EXISTING = 'EXISTING',
}

export enum QueryType {
  ISSUE = 'ISSUE',
  CASUE = 'CAUSE',
  ROUTINE = 'ROUTINE',
}

export interface File {
  id: number;
  url: string;
  filename: string;
}

export interface TemplateResourceImage extends File {
  businessId: number;
  name: string;
}

export interface TemplateResourceImages {
  templateID: number;
  issueImages: TemplateResourceImage[];
  causeImages: TemplateResourceImage[];
  machineImages: TemplateResourceImage[];
}

export interface Machine {
  images?: File[];
  documents: File[];
  machineID: string;
  name: string;
  alias?: string;
  location?: string;
  serviceNumber?: string;
  templateID: string;
  templateName?: string;
  type?: string;
  manufacturer?: string;
  serialNumber?: string;
  constructionDate?: string;
  acceptanceDate?: string;
  description?: string;
  image?: string;
}

export interface Issue {
  images?: File[];
  issueID: string;
  image?: string;
  name: string;
  labels?: string[];
  tools?: string[];
  qualification?: string;
  description?: string;
  templateID?: string;
  templateName?: string;
  activeStatus: ActiveStatus;
  isSuggestion: YesOrNo;
  suggestionStatus?: SuggestionStatus;
  suggestionSubmitterID?: string;
  suggestionSubmitterName?: string;
  suggestionSubmittedTime?: string;
  suggestionReviewerID?: string;
  suggestionReviewerName?: string;
  suggestionReviewTime?: string;
  suggestionReviewDescription?: string;
  embeddingDescription?: string[];
  classificationReason?: string;
  similarityScore?: number;
}
export interface Cause {
  images?: File[];
  causeID: string;
  image?: string;
  name: string;
  labels?: string[];
  tools?: string[];
  qualification?: string;
  description?: string;
  templateID?: string;
  templateName?: string;
  activeStatus: ActiveStatus;
  isSuggestion: YesOrNo;
  suggestionStatus?: SuggestionStatus;
  suggestionSubmitterID?: string;
  suggestionSubmitterName?: string;
  suggestionSubmittedTime?: string;
  suggestionReviewerID?: string;
  suggestionReviewerName?: string;
  suggestionReviewTime?: string;
  suggestionReviewDescription?: string;
}

export interface Step {
  images?: File[];
  routineStepID: string;
  routineID: string;
  name: string;
  image?: string;
  description?: string;
  signNames?: string;
}

export interface Routine {
  images?: File[];
  routineID: string;
  name: string;
  linkedCause?: Cause;
  description?: string;
  templateID?: string;
  templateName?: string;
  routineSteps?: Step[]; // TODO: confirm step info
  signNames?: string;
  activeStatus: ActiveStatus;
  isSuggestion: YesOrNo;
  suggestionStatus?: SuggestionStatus;
  suggestionSubmitterID?: string;
  suggestionSubmitterName?: string;
  suggestionSubmittedTime?: string;
  suggestionReviewerID?: string;
  suggestionReviewerName?: string;
  suggestionReviewTime?: string;
  suggestionReviewDescription?: string;
}

export interface SolutionGrid {
  cause: Cause;
  issues: (Issue & {
    frequency?: Frequency;
  })[];
}

export interface SolutionGridVersion {
  versionID: string;
  versionName: string;
  templateID: string;
  versionNumber: string;
  updateTime: string;
  createTime: string;
  submitterID: string;
  submitterName: string;
  description: string;
  effective: boolean;
}

export interface SolutionGridItem {
  gridID: string;
  causeID: string;
  causeName: string;
  issueID: string;
  issueName: string;
  relevanceRatio?: Frequency;
  templateID: string;
  activeStatus: ActiveStatus;
  versionID?: string | number;
}

export interface SolutionGridResponseDto {
  solutionGridDtoList: SolutionGridItem[];
  solutionGridVersionDtoList?: SolutionGridVersion[];
  currentVersionDto?: SolutionGridVersion;
}

export interface SolutionGridRequestDto {
  newOrExisting: NewOrExisting;
  versionName?: string;
  versionID?: string;
  versionDescription?: string;
  solutionGridDtoList: SolutionGridItem[];
  solutionGridVersionDtoList?: SolutionGridVersion[];
  currentVersionDto?: SolutionGridVersion;
}

export interface QueryParams {
  search?: string;
  pageSize?: number;
  pageNumber?: number;
  sortBy?: string;
  sortDirection?: 'ASC' | 'DESC';
  templateId?: number;
}

export interface Template {
  images?: File[];
  templateID: string;
  name: string;
  type?: string;
  manufacturer?: string;
  machines: Machine[];
  issues: Issue[];
  causes: Cause[];
  solutionGrid: SolutionGridItem[];
  routines?: Routine[];
  documents?: File[];
}

export enum ROLES {
  ROLE_ADMIN = 'ROLE_ADMIN',
  ROLE_WORKER = 'ROLE_WORKER',
  ROLE_EXPERT = 'ROLE_EXPERT',
  ROLE_SUPERADMIN = 'ROLE_SUPERADMIN',
}

export interface User {
  companyName: string;
  userId: string;
  username: string;
  firstName?: string;
  lastName?: string;
  companyID?: string;
  email?: string;
  password?: string;
  isActive: boolean;
  role?: ROLES;
  roles: {
    id?: number;
    name: ROLES;
  }[];
  licenseType?: string;
  licenseExpireDate?: string;
  licenseRemainingDays?: number;
}

export enum LicenseStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  TERMINATED = 'TERMINATED',
  IN_COMING = 'IN_COMING', // For FE only
}

export enum LicenseType {
  PRODUCTION = 'PRODUCTION',
  TRIAL = 'TRIAL',
}

export enum LicenseEntitlement {
  CORE = 'CORE',
  AI_DOCUMENT = 'AI_DOCUMENT',
  AI_VOICE = 'AI_VOICE',
  AI_IMAGE = 'AI_IMAGE',
  USERPROFILE = 'USERPROFILE',
  INSIGHT_PEOPLE = 'INSIGHT_PEOPLE',
}

export enum LicenseConversionApproach {
  AUTO_CONVERSION = 'AUTO_CONVERSION',
  MANU_CONVERSION = 'MANU_CONVERSION',
}

export interface License {
  licenseKey: string;
  companyID: string;
  companyName: string;
  entitlement: LicenseEntitlement;
  startDate?: string;
  endDate?: string;
  extraTemplates?: string;
  extraMachines?: string;
  usedTemplates?: string;
  usedMachines?: string;
  type: LicenseType;
  status?: LicenseStatus;
}

export enum CompanyType {
  SOLVV = 'SOLVV',
  DEMO = 'DEMO',
  REAL = 'REAL',
}

export interface Company {
  companyID: string;
  name: string;
  description?: string;
  address?: string;
  contact?: string;
  email?: string;
  type?: CompanyType;
  licenseStatus?: LicenseStatus;
  licenseType?: LicenseType;
}

export interface CreateLicensePayload {
  companyID: string;
  entitlement: LicenseEntitlement;
  startDate: string;
  endDate: string;
  type: LicenseType;
}
export interface Notification {
  issueList: Issue[];
  causeList: Cause[];
  routineList: Routine[];
}

export enum CloseAction {
  BACK = 'BACK',
  CLOSE = 'CLOSE',
}

export enum UploadBusinessType {
  MACHINE = 'MACHINE',
  TEMPLATE = 'TEMPLATE',
  ISSUE = 'ISSUE',
  CAUSE = 'CAUSE',
  ROUTINE = 'ROUTINE',
  ROUTINECAUSE = 'ROUTINECAUSE',
  ROUTINESTEP = 'ROUTINESTEP',
  SOLUTIONGRID = 'SOLUTIONGRID',
}

export enum UploadFileType {
  IMAGE = 'IMAGE',
  DOCUMENT = 'DOCUMENT',
}
